import React, { useState, useEffect } from "react";
import {
    Grid,
    TextField,
    Typography,
    Box,
    CircularProgress,
    Button,
} from "@mui/material";
import { submitConfiguration, getConfiguration } from "../../api";
import "./ConfigureCredentials.css";
import "./APITesting.css";
import { toast } from "react-toastify";
import { messageTypeOptions } from "./FormComponents";

const ConfigureCredentials = ({ onNext }) => {
    // New state for the questions - using objects to track Yes/No for each message type
    const [supportedMessages, setSupportedMessages] = useState({});
    const [supportsAuditAPI, setSupportsAuditAPI] = useState("");
    const [supportsPropertyCreation, setSupportsPropertyCreation] =
        useState("");

    const [pushConfig, setPushConfig] = useState({
        baseUrl: "",
        username: "",
        password: "",
        property: null,
    });

    const [auditConfig, setAuditConfig] = useState({
        baseUrl: "",
        username: "",
        password: "",
        property: null,
    });

    const [submitLoading, setSubmitLoading] = useState(false);

    useEffect(() => {
        const fetchConfiguration = async () => {
            try {
                const clientId = localStorage.getItem("clientInfo")
                    ? JSON.parse(localStorage.getItem("clientInfo"))
                          .sandboxClientId
                    : null;

                if (!clientId) {
                    console.error("Client ID not found");
                    return;
                }

                const response = await getConfiguration(clientId);

                if (response.status === 200 && response.data) {
                    // Set API configuration fields
                    setSupportsAuditAPI(
                        response.data.isAuditApiAvailable ? "yes" : "no",
                    );
                    setSupportsPropertyCreation(
                        response.data.isPropertyCreationAllowed ? "yes" : "no",
                    );

                    // Map allowed message types to supported messages
                    const messageTypeMapping = {
                        PROPERTY: "Property",
                        RATEPLAN: "Rateplan",
                        ROOMTYPE: "Roomtype",
                        TAX: "Taxes",
                        POLICY: "Cancellation Policy",
                        PRODUCT: "Products",
                        OFFER: "Offer",
                    };

                    const mappedSupportedMessages = {};
                    if (
                        response.data.allowedMessageTypes &&
                        Array.isArray(response.data.allowedMessageTypes)
                    ) {
                        // If array is present, mark messages as Yes if in array, No if not
                        const allMessageTypes = [
                            "Property",
                            "Rateplan",
                            "Roomtype",
                            "Taxes",
                            "Cancellation Policy",
                            "Products",
                            "Offer",
                        ];
                        allMessageTypes.forEach((messageType) => {
                            const apiType = Object.keys(
                                messageTypeMapping,
                            ).find(
                                (key) =>
                                    messageTypeMapping[key] === messageType,
                            );
                            if (
                                apiType &&
                                response.data.allowedMessageTypes.includes(
                                    apiType,
                                )
                            ) {
                                mappedSupportedMessages[messageType] = "yes";
                            } else {
                                mappedSupportedMessages[messageType] = "no";
                            }
                        });
                    }
                    // If allowedMessageTypes is null or not present, don't set any values
                    setSupportedMessages(mappedSupportedMessages);

                    // Set API configuration fields
                    setPushConfig({
                        baseUrl: response.data.clientPushUrl || "",
                        username: response.data.clientPushUsername || "",
                        password: response.data.clientPushPassword || "",
                        property: null,
                    });

                    setAuditConfig({
                        baseUrl: response.data.clientAuditUrl || "",
                        username: response.data.clientAuditUsername || "",
                        password: response.data.clientAuditPassword || "",
                        property: null,
                    });
                }
            } catch (error) {
                console.error("Error fetching configuration:", error);
            }
        };

        fetchConfiguration();
    }, []);

    const handleInputChange = (field, value, configType) => {
        if (configType === "push") {
            setPushConfig((prev) => ({
                ...prev,
                [field]: value,
            }));
        } else {
            setAuditConfig((prev) => ({
                ...prev,
                [field]: value,
            }));
        }
    };

    const handleSupportedMessageChange = (messageType, value) => {
        // Define the mutually exclusive groups
        const offerRelatedTypes = [
            "Rateplan",
            "Taxes",
            "Cancellation Policy",
            "Products",
        ];

        setSupportedMessages((prev) => {
            const updated = { ...prev, [messageType]: value };

            // Only apply mutual exclusion when selecting "yes"
            if (value === "yes") {
                if (messageType === "Offer") {
                    // If Offer is selected as Yes, set all offerRelatedTypes that are "yes" to "no"
                    offerRelatedTypes.forEach((type) => {
                        if (prev[type] === "yes") {
                            updated[type] = "no";
                        }
                    });
                } else if (offerRelatedTypes.includes(messageType)) {
                    // If any offerRelatedType is selected as Yes, and Offer is "yes", set Offer to "no"
                    if (prev["Offer"] === "yes") {
                        updated["Offer"] = "no";
                    }
                }
            }

            return updated;
        });
    };

    const handleSupportsAuditAPIChange = (value) => {
        setSupportsAuditAPI(value);
    };

    const handleSupportsPropertyCreationChange = (value) => {
        setSupportsPropertyCreation(value);
    };

    const validateFields = () => {
        // Check for missing selections
        const selectedMessages = Object.values(supportedMessages).filter(
            (value) => value === "yes",
        );
        if (!supportsAuditAPI) {
            toast.error("Please select Yes/No for Supports Audit API");
            return false;
        }
        if (!supportsPropertyCreation) {
            toast.error("Please select Yes/No for Supports Property Creation");
            return false;
        }

        if (selectedMessages.length === 0) {
            toast.error("Please select at least one supported message type");
            return false;
        }

        // Check for missing push API fields
        if (!pushConfig.baseUrl.trim()) {
            toast.error("Please enter Push API Base URL");
            return false;
        }
        if (!pushConfig.username.trim()) {
            toast.error("Please enter Push API Username");
            return false;
        }
        if (!pushConfig.password.trim()) {
            toast.error("Please enter Push API Password");
            return false;
        }

        // Check for missing audit API fields if audit is supported
        if (supportsAuditAPI === "yes") {
            if (!auditConfig.baseUrl.trim()) {
                toast.error("Please enter Audit API Base URL");
                return false;
            }
            if (!auditConfig.username.trim()) {
                toast.error("Please enter Audit API Username");
                return false;
            }
            if (!auditConfig.password.trim()) {
                toast.error("Please enter Audit API Password");
                return false;
            }
        }

        return true;
    };

    const handleNextClick = async () => {
        const isValid = validateFields();
        if (!isValid) {
            return;
        }

        setSubmitLoading(true);

        // Only include message types with "yes" value
        const selectedMessages = Object.keys(supportedMessages).filter(
            (key) => supportedMessages[key] === "yes",
        );

        // Reverse mapping for API format
        const reverseMessageTypeMapping = {
            Property: "PROPERTY",
            Rateplan: "RATEPLAN",
            Roomtype: "ROOMTYPE",
            Taxes: "TAX",
            "Cancellation Policy": "POLICY",
            Products: "PRODUCT",
            Offer: "OFFER",
        };

        const apiMessageTypes = selectedMessages
            .map((messageType) => reverseMessageTypeMapping[messageType])
            .filter(Boolean);

        const clientInfo = JSON.parse(
            localStorage.getItem("clientInfo") || "{}",
        );
        const idTokenData = JSON.parse(
            localStorage.getItem("idTokenData") || "{}",
        );
        const configData = {
            clientId: clientInfo.sandboxClientId || null,
            productionClientId: clientInfo.productionClientId || null,
            clientCode: idTokenData.clientCode,
            isAuditApiAvailable: supportsAuditAPI === "yes",
            isPropertyCreationAllowed: supportsPropertyCreation === "yes",
            allowedMessageTypes: apiMessageTypes,
            clientPushUrl: pushConfig.baseUrl,
            clientPushUsername: pushConfig.username,
            clientPushPassword: pushConfig.password,
            clientAuditUrl:
                supportsAuditAPI === "yes" ? auditConfig.baseUrl : "",
            clientAuditUsername:
                supportsAuditAPI === "yes" ? auditConfig.username : "",
            clientAuditPassword:
                supportsAuditAPI === "yes" ? auditConfig.password : "",
        };

        try {
            const response = await submitConfiguration(configData);
            if (response.status === 200) {
                if (response.data.status) {
                    // Store configuration data for use in Content API step
                    const apiConfiguration = {
                        supportedMessages: selectedMessages,
                        supportsAuditAPI: supportsAuditAPI,
                        supportsPropertyCreation: supportsPropertyCreation,
                    };
                    localStorage.setItem(
                        "apiConfiguration",
                        JSON.stringify(apiConfiguration),
                    );
                    onNext();
                } else {
                    toast.error(response.data.message);
                    console.error("Failed to submit configuration:", response);
                }
            } else {
                console.error("Failed to submit configuration:", response);
            }
        } catch (error) {
            console.error("Error submitting configuration:", error);
        } finally {
            setSubmitLoading(false);
        }
    };

    return (
        <Box className="configure-credentials">
            <Typography variant="h6" sx={{ mb: 3, color: "#072233" }}>
                Configure your API credentials to perform test API interactions.
            </Typography>

            {/* API Capabilities Section */}
            <div className="preferences-section">
                <h3 className="preferences-section-title">API Capabilities</h3>

                {/* Supports Audit API and Property Creation in 2 columns */}
                <Grid container spacing={2} sx={{ mb: 4 }}>
                    {/* Supports Audit API */}
                    <Grid item xs={12} md={6}>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                padding: "16px",
                                border: "1px solid #E5E7EB",
                                borderRadius: "8px",
                                backgroundColor: "#fff",
                            }}
                        >
                            <div>
                                <div
                                    style={{
                                        fontWeight: 600,
                                        color: "#374151",
                                        fontSize: 14,
                                    }}
                                >
                                    Supports Audit API *
                                </div>
                                <div style={{ color: "#6b7280", fontSize: 12 }}>
                                    Indicate whether your system supports audit
                                    API functionality for tracking and
                                    monitoring purposes.
                                </div>
                            </div>
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                }}
                            >
                                <div
                                    style={{
                                        width: 1,
                                        height: 36,
                                        background: "#E5E7EB",
                                        margin: "0 8px",
                                    }}
                                />
                                <Button
                                    variant={
                                        supportsAuditAPI === "yes"
                                            ? "contained"
                                            : "outlined"
                                    }
                                    size="small"
                                    className={`choice-button ${
                                        supportsAuditAPI === "yes"
                                            ? "selected"
                                            : ""
                                    }`}
                                    onClick={() =>
                                        handleSupportsAuditAPIChange("yes")
                                    }
                                >
                                    Yes
                                </Button>
                                <Button
                                    variant={
                                        supportsAuditAPI === "no"
                                            ? "contained"
                                            : "outlined"
                                    }
                                    size="small"
                                    className={`choice-button ${
                                        supportsAuditAPI === "no"
                                            ? "selected"
                                            : ""
                                    }`}
                                    onClick={() =>
                                        handleSupportsAuditAPIChange("no")
                                    }
                                >
                                    No
                                </Button>
                            </div>
                        </div>
                    </Grid>

                    {/* Supports Property Creation */}
                    <Grid item xs={12} md={6}>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                padding: "16px",
                                border: "1px solid #E5E7EB",
                                borderRadius: "8px",
                                backgroundColor: "#fff",
                            }}
                        >
                            <div>
                                <div
                                    style={{
                                        fontWeight: 600,
                                        color: "#374151",
                                        fontSize: 14,
                                    }}
                                >
                                    Supports Property Creation *
                                </div>
                                <div style={{ color: "#6b7280", fontSize: 12 }}>
                                    Indicate whether your system supports
                                    dynamic property creation and management
                                    capabilities.
                                </div>
                            </div>
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                }}
                            >
                                <div
                                    style={{
                                        width: 1,
                                        height: 36,
                                        background: "#E5E7EB",
                                        margin: "0 8px",
                                    }}
                                />
                                <Button
                                    variant={
                                        supportsPropertyCreation === "yes"
                                            ? "contained"
                                            : "outlined"
                                    }
                                    size="small"
                                    className={`choice-button ${
                                        supportsPropertyCreation === "yes"
                                            ? "selected"
                                            : ""
                                    }`}
                                    onClick={() =>
                                        handleSupportsPropertyCreationChange(
                                            "yes",
                                        )
                                    }
                                >
                                    Yes
                                </Button>
                                <Button
                                    variant={
                                        supportsPropertyCreation === "no"
                                            ? "contained"
                                            : "outlined"
                                    }
                                    size="small"
                                    className={`choice-button ${
                                        supportsPropertyCreation === "no"
                                            ? "selected"
                                            : ""
                                    }`}
                                    onClick={() =>
                                        handleSupportsPropertyCreationChange(
                                            "no",
                                        )
                                    }
                                >
                                    No
                                </Button>
                            </div>
                        </div>
                    </Grid>
                </Grid>

                {/* Supported Messages - Redesigned like client preferences */}
                <Box sx={{ mb: 4 }}>
                    <div
                        style={{
                            fontWeight: 600,
                            color: "#374151",
                            fontSize: 14,
                        }}
                    >
                        Supported Messages *
                        <Typography
                            key={`supported-messages-desc`}
                            className="preferences-section-description"
                            variant="h6"
                        >
                            Select the message types that your system supports
                            for API interactions.
                        </Typography>
                    </div>

                    {/* Individual message types in 2 columns */}
                    <Grid container spacing={2}>
                        {messageTypeOptions.map((option) => (
                            <Grid item xs={12} md={6} key={option.value}>
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        padding: "16px",
                                        border: "1px solid #E5E7EB",
                                        borderRadius: "8px",
                                        backgroundColor: "#fff",
                                    }}
                                >
                                    <div>
                                        <div
                                            style={{
                                                fontWeight: 600,
                                                color: "#374151",
                                                fontSize: 14,
                                            }}
                                        >
                                            {option.label}
                                        </div>
                                    </div>
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 8,
                                        }}
                                    >
                                        <div
                                            style={{
                                                width: 1,
                                                height: 36,
                                                background: "#E5E7EB",
                                                margin: "0 8px",
                                            }}
                                        />
                                        <Button
                                            variant={
                                                supportedMessages[
                                                    option.value
                                                ] === "yes"
                                                    ? "contained"
                                                    : "outlined"
                                            }
                                            size="small"
                                            className={`choice-button ${
                                                supportedMessages[
                                                    option.value
                                                ] === "yes"
                                                    ? "selected"
                                                    : ""
                                            }`}
                                            onClick={() =>
                                                handleSupportedMessageChange(
                                                    option.value,
                                                    "yes",
                                                )
                                            }
                                        >
                                            Yes
                                        </Button>
                                        <Button
                                            variant={
                                                supportedMessages[
                                                    option.value
                                                ] === "no"
                                                    ? "contained"
                                                    : "outlined"
                                            }
                                            size="small"
                                            className={`choice-button ${
                                                supportedMessages[
                                                    option.value
                                                ] === "no"
                                                    ? "selected"
                                                    : ""
                                            }`}
                                            onClick={() =>
                                                handleSupportedMessageChange(
                                                    option.value,
                                                    "no",
                                                )
                                            }
                                        >
                                            No
                                        </Button>
                                    </div>
                                </div>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            </div>

            {/* API Configuration Section - Inside the grey box */}
            <Box sx={{ bgcolor: "#F8F9FB", p: 3, borderRadius: 2 }}>
                <Typography
                    variant="subtitle1"
                    sx={{
                        mb: 3,
                        color: "#072233",
                        fontWeight: 500,
                        fontSize: "16px",
                    }}
                >
                    API Configuration
                </Typography>

                <Grid container spacing={3}>
                    {/* Push API Configuration */}
                    <Grid item xs={6}>
                        <Typography
                            variant="subtitle1"
                            sx={{
                                mb: 2,
                                color: "#072233",
                                fontWeight: 500,
                                fontSize: "16px",
                            }}
                        >
                            Push API configuration
                        </Typography>
                        <Box sx={{ mb: 2 }}>
                            <TextField
                                fullWidth
                                placeholder="value"
                                label="Base URL"
                                value={pushConfig.baseUrl}
                                onChange={(e) =>
                                    handleInputChange(
                                        "baseUrl",
                                        e.target.value,
                                        "push",
                                    )
                                }
                                variant="outlined"
                                size="small"
                                sx={{
                                    bgcolor: "#fff",
                                    "& .MuiOutlinedInput-root": {
                                        "& fieldset": {
                                            borderColor: "#E5E7EB",
                                        },
                                        "& input": {
                                            fontSize: "14px",
                                        },
                                    },
                                    "& .MuiInputLabel-root": {
                                        fontSize: "14px",
                                        "&.MuiInputLabel-shrink": {
                                            transform:
                                                "translate(14px, -9px) scale(0.75)",
                                            backgroundColor: "#fff",
                                            padding: "0 4px",
                                        },
                                    },
                                }}
                            />
                        </Box>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    placeholder="value"
                                    label="Username"
                                    value={pushConfig.username}
                                    onChange={(e) =>
                                        handleInputChange(
                                            "username",
                                            e.target.value,
                                            "push",
                                        )
                                    }
                                    variant="outlined"
                                    size="small"
                                    sx={{
                                        bgcolor: "#fff",
                                        "& .MuiOutlinedInput-root": {
                                            "& fieldset": {
                                                borderColor: "#E5E7EB",
                                            },
                                            "& input": {
                                                fontSize: "14px",
                                            },
                                        },
                                        "& .MuiInputLabel-root": {
                                            fontSize: "14px",
                                            "&.MuiInputLabel-shrink": {
                                                transform:
                                                    "translate(14px, -9px) scale(0.75)",
                                                backgroundColor: "#fff",
                                                padding: "0 4px",
                                            },
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    placeholder="value"
                                    label="Password"
                                    type="password"
                                    value={pushConfig.password}
                                    onChange={(e) =>
                                        handleInputChange(
                                            "password",
                                            e.target.value,
                                            "push",
                                        )
                                    }
                                    variant="outlined"
                                    size="small"
                                    sx={{
                                        bgcolor: "#fff",
                                        "& .MuiOutlinedInput-root": {
                                            "& fieldset": {
                                                borderColor: "#E5E7EB",
                                            },
                                            "& input": {
                                                fontSize: "14px",
                                            },
                                        },
                                        "& .MuiInputLabel-root": {
                                            fontSize: "14px",
                                            "&.MuiInputLabel-shrink": {
                                                transform:
                                                    "translate(14px, -9px) scale(0.75)",
                                                backgroundColor: "#fff",
                                                padding: "0 4px",
                                            },
                                        },
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </Grid>

                    {/* Audit API Configuration */}
                    <Grid item xs={6}>
                        <Typography
                            variant="subtitle1"
                            sx={{
                                mb: 2,
                                color: "#072233",
                                fontWeight: 500,
                                fontSize: "16px",
                            }}
                        >
                            Audit API configuration
                            {supportsAuditAPI === "no" && (
                                <Typography
                                    component="span"
                                    sx={{
                                        ml: 1,
                                        fontSize: "12px",
                                        color: "#666",
                                        fontWeight: "normal",
                                    }}
                                >
                                    (Not supported)
                                </Typography>
                            )}
                        </Typography>
                        <Box sx={{ mb: 2 }}>
                            <TextField
                                fullWidth
                                placeholder="value"
                                label="Base URL"
                                value={auditConfig.baseUrl}
                                onChange={(e) =>
                                    handleInputChange(
                                        "baseUrl",
                                        e.target.value,
                                        "audit",
                                    )
                                }
                                variant="outlined"
                                size="small"
                                disabled={supportsAuditAPI === "no"}
                                sx={{
                                    bgcolor:
                                        supportsAuditAPI === "no"
                                            ? "#f5f5f5"
                                            : "#fff",
                                    "& .MuiOutlinedInput-root": {
                                        "& fieldset": {
                                            borderColor: "#E5E7EB",
                                        },
                                        "& input": {
                                            fontSize: "14px",
                                        },
                                    },
                                    "& .MuiInputLabel-root": {
                                        fontSize: "14px",
                                        "&.MuiInputLabel-shrink": {
                                            transform:
                                                "translate(14px, -9px) scale(0.75)",
                                            backgroundColor:
                                                supportsAuditAPI === "no"
                                                    ? "#f5f5f5"
                                                    : "#fff",
                                            padding: "0 4px",
                                        },
                                    },
                                }}
                            />
                        </Box>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    placeholder="value"
                                    label="Username"
                                    value={auditConfig.username}
                                    onChange={(e) =>
                                        handleInputChange(
                                            "username",
                                            e.target.value,
                                            "audit",
                                        )
                                    }
                                    variant="outlined"
                                    size="small"
                                    disabled={supportsAuditAPI === "no"}
                                    sx={{
                                        bgcolor:
                                            supportsAuditAPI === "no"
                                                ? "#f5f5f5"
                                                : "#fff",
                                        "& .MuiOutlinedInput-root": {
                                            "& fieldset": {
                                                borderColor: "#E5E7EB",
                                            },
                                            "& input": {
                                                fontSize: "14px",
                                            },
                                        },
                                        "& .MuiInputLabel-root": {
                                            fontSize: "14px",
                                            "&.MuiInputLabel-shrink": {
                                                transform:
                                                    "translate(14px, -9px) scale(0.75)",
                                                backgroundColor:
                                                    supportsAuditAPI === "no"
                                                        ? "#f5f5f5"
                                                        : "#fff",
                                                padding: "0 4px",
                                            },
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    placeholder="value"
                                    label="Password"
                                    type="password"
                                    value={auditConfig.password}
                                    onChange={(e) =>
                                        handleInputChange(
                                            "password",
                                            e.target.value,
                                            "audit",
                                        )
                                    }
                                    variant="outlined"
                                    size="small"
                                    disabled={supportsAuditAPI === "no"}
                                    sx={{
                                        bgcolor:
                                            supportsAuditAPI === "no"
                                                ? "#f5f5f5"
                                                : "#fff",
                                        "& .MuiOutlinedInput-root": {
                                            "& fieldset": {
                                                borderColor: "#E5E7EB",
                                            },
                                            "& input": {
                                                fontSize: "14px",
                                            },
                                        },
                                        "& .MuiInputLabel-root": {
                                            fontSize: "14px",
                                            "&.MuiInputLabel-shrink": {
                                                transform:
                                                    "translate(14px, -9px) scale(0.75)",
                                                backgroundColor:
                                                    supportsAuditAPI === "no"
                                                        ? "#f5f5f5"
                                                        : "#fff",
                                                padding: "0 4px",
                                            },
                                        },
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>

                <Box
                    sx={{ display: "flex", justifyContent: "flex-end", mt: 4 }}
                >
                    <button
                        className="next-button"
                        onClick={handleNextClick}
                        disabled={submitLoading}
                    >
                        {submitLoading ? (
                            <CircularProgress size={24} color="inherit" />
                        ) : (
                            "Next step >"
                        )}
                    </button>
                </Box>
            </Box>
        </Box>
    );
};

export default ConfigureCredentials;
